import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import api from '../services/api';
import { formatExchangeName } from '../utils/exchangeDisplay';
import './FundingRateHistory.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// scheduler のエントリー判定 (tradingScheduler.js:321) と同じ 7日 (168h) 基準で統一
const LOOKBACK_HOURS = 168;

const FundingRateHistory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [exchanges, setExchanges] = useState([]);
  const [currencies, setCurrencies] = useState({});
  const [exchange, setExchange] = useState(searchParams.get('exchange') || '');
  const [coin, setCoin] = useState(searchParams.get('coin') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [apiKeyRes, currRes] = await Promise.all([
          api.get('/apikey'),
          api.get('/trading/currencies'),
        ]);
        const exList = (apiKeyRes.data.apiKeys || []).map(k => k.exchange);
        setExchanges(exList);
        setCurrencies(currRes.data.currencies || {});
        if (!exchange && exList.length > 0) {
          setExchange(exList[0]);
        }
      } catch (e) {
        console.error('init error:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 取引所が変わったら、その取引所で利用可能な通貨の先頭を選ぶ (現在の coin が無効なら)
  useEffect(() => {
    if (!exchange) return;
    const list = currencies[exchange] || [];
    if (list.length === 0) return;
    if (!coin || !list.find(c => c.symbol === coin)) {
      setCoin(list[0].symbol);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchange, currencies]);

  // URL 反映
  useEffect(() => {
    if (!exchange || !coin) return;
    setSearchParams({ exchange, coin }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchange, coin]);

  // データ取得
  useEffect(() => {
    if (!exchange || !coin) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(''); setData(null);
      try {
        const res = await api.get('/trading/funding-rate-history', {
          params: { exchange, coin, lookbackHours: LOOKBACK_HOURS },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [exchange, coin]);

  const chartData = useMemo(() => {
    const samples = data?.samples || [];
    return {
      labels: samples.map(s => new Date(s.time).toLocaleString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit' })),
      datasets: [
        {
          label: 'FR (%)',
          data: samples.map(s => s.fundingRate * 100),
          borderColor: '#f7931a',
          backgroundColor: 'rgba(247, 147, 26, 0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 2,
        },
      ],
    };
  }, [data]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { color: '#a0a0a0' } },
      tooltip: {
        backgroundColor: '#16213e', titleColor: '#e0e0e0', bodyColor: '#e0e0e0',
        callbacks: {
          label: (ctx) => `FR: ${ctx.parsed.y.toFixed(6)}%`,
        },
      },
      annotation: undefined,
    },
    scales: {
      x: { grid: { color: 'rgba(42, 42, 74, 0.5)' }, ticks: { color: '#a0a0a0', maxTicksLimit: 12 } },
      y: {
        grid: { color: 'rgba(42, 42, 74, 0.5)' },
        ticks: { color: '#a0a0a0', callback: (v) => v.toFixed(4) + '%' },
      },
    },
  }), []);

  const stats = data?.stats;
  const fmtPct = (v) => v == null ? '-' : (v * 100).toFixed(6) + '%';
  const fmtFraction = (v) => v == null ? '-' : (v * 100).toFixed(1) + '%';
  const fmtDays = (v) => v == null ? '-' : v.toFixed(1) + ' 日';

  return (
    <div className="funding-rate-history">
      <div className="page-header">
        <h1>FRレート推移</h1>
      </div>

      <div className="filter-row">
        <select className="filter-select" value={exchange} onChange={(e) => setExchange(e.target.value)}>
          {exchanges.length === 0 && <option value="">取引所を登録してください</option>}
          {exchanges.map(ex => (
            <option key={ex} value={ex}>{formatExchangeName(ex)}</option>
          ))}
        </select>
        <select className="filter-select" value={coin} onChange={(e) => setCoin(e.target.value)}>
          {(currencies[exchange] || []).map(c => (
            <option key={c.symbol} value={c.symbol}>{c.symbol} ({c.name})</option>
          ))}
        </select>
        <span className="lookback-fixed">直近 7 日 (エントリー判定基準)</span>
      </div>

      {error && <div className="error-message">{error}</div>}
      {loading ? (
        <div className="page-loading">読み込み中...</div>
      ) : data ? (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">次回予測 FR</span>
              <span className={`stat-value ${stats.nextFundingRate >= 0 ? 'profit' : 'loss'}`}>
                {stats.nextFundingRate === null ? '取得不可' : fmtPct(stats.nextFundingRate)}
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-label">サンプル数</span>
              <span className="stat-value">{stats.count}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">平均 FR (1サンプル)</span>
              <span className={`stat-value ${stats.avg >= 0 ? 'profit' : 'loss'}`}>{fmtPct(stats.avg)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">累積 FR</span>
              <span className={`stat-value ${stats.sum >= 0 ? 'profit' : 'loss'}`}>{fmtPct(stats.sum)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">プラス比率</span>
              <span className="stat-value">{fmtFraction(stats.positiveRatio)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">
                ブレークイブン
                <span
                  className="help-icon"
                  data-tooltip={
                    `往復手数料を平均 FR で取り戻すまでの想定日数\n` +
                    `往復手数料: ${stats.roundTripFee != null ? (stats.roundTripFee * 100).toFixed(2) + '%' : '-'}\n` +
                    `1日あたり徴収回数: ${stats.settlementsPerDay ?? '-'} 回\n` +
                    `計算式: ${stats.roundTripFee != null ? (stats.roundTripFee * 100).toFixed(2) + '%' : '-'} ÷ (平均FR × ${stats.settlementsPerDay ?? '-'} 回/日)`
                  }
                  aria-label="ブレークイブン日数の計算"
                  tabIndex={0}
                >?</span>
              </span>
              <span className={`stat-value ${stats.entryAllowed ? 'profit' : ''}`}>
                {stats.breakEvenDays != null ? fmtDays(stats.breakEvenDays) : '—'}
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-label">
                エントリー判定
                <span
                  className="help-icon"
                  data-tooltip={
                    'エントリー条件 (両方満たすと OK)\n' +
                    '① 次回予測 FR > 0\n' +
                    '② 直近 7 日 (168h) の平均 FR > 0'
                  }
                  aria-label="エントリー判定の条件"
                  tabIndex={0}
                >?</span>
              </span>
              <span className={`stat-value ${stats.entryAllowed ? 'profit' : 'loss'}`}>
                {stats.entryAllowed
                  ? 'OK'
                  : `SKIP (${!stats.condCurrent ? '①' : ''}${!stats.condCurrent && !stats.condRegime ? '+' : ''}${!stats.condRegime ? '②' : ''})`}
              </span>
            </div>
          </div>

          <div className="chart-container">
            <h2 className="section-title">FR 時系列</h2>
            <div className="chart-wrapper">
              {(data.samples || []).length > 0 ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <div className="empty-message">サンプルがありません</div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default FundingRateHistory;
